import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { getRoomByCode, submitScore } from '../services/room-api.js';
import { useRoomSocket } from '../hooks/useRoomSocket.js';
import { useAuth } from '../hooks/useAuth.js';
import Loading from '../components/common/Loading.jsx';
import {
  CARD_H, GAP_V, CARD_W, COL_GAP, LABEL_H,
  isDoubleBye, buildRoundsWithByes, cardTop, cardCenterY,
  buildConnectorLines, applyScoreResolved,
} from '../utils/bracketUtils.js';
import './RoomPage.css';

// ─── Score Submit Modal ────────────────────────────────────────────────────────

function ScorerCodeModal({ onSubmit, onClose }) {
  const [code, setCode] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (code.trim().length === 6) onSubmit(code.trim().toUpperCase());
  };

  return (
    <div className="room-modal-overlay" onClick={onClose}>
      <div className="room-modal" onClick={e => e.stopPropagation()}>
        <h2>Scorer Code Required</h2>
        <p className="room-modal-match">Ask the organiser for the 6-character code.</p>
        <form onSubmit={handleSubmit}>
          <input
            className="room-input room-input--scorer-code"
            type="text"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="K7M2X3"
            autoFocus
          />
          <div className="room-modal-actions">
            <button type="button" className="room-btn room-btn--secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="room-btn room-btn--primary" disabled={code.trim().length !== 6}>Continue</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ScoreModal({ match, roomCode, scorerCode, onClose, onSubmitted, onScorerCodeInvalid }) {
  const [name, setName] = useState('');
  const [score1, setScore1] = useState('');
  const [score2, setScore2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError('Please enter your name');
    if (score1 === '' || score2 === '') return setError('Please enter both scores');

    setSubmitting(true);
    setError(null);
    try {
      await submitScore({
        code: roomCode,
        matchId: match.matchId,
        submittedBy: name.trim(),
        score1: Number(score1),
        score2: Number(score2),
        scorerCode: scorerCode || undefined,
      });
      onSubmitted();
      onClose();
    } catch (err) {
      if (err.status === 403) {
        onScorerCodeInvalid?.();
        onClose();
        return;
      }
      setError('Failed to submit. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="room-modal-overlay" onClick={onClose}>
      <div className="room-modal" onClick={e => e.stopPropagation()}>
        <h2>Submit Score</h2>
        <p className="room-modal-match">
          {match.player1Name || 'TBD'} vs {match.player2Name || 'TBD'}
        </p>

        <form onSubmit={handleSubmit}>
          <label className="room-label">
            Your name
            <input
              className="room-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Who are you?"
              autoFocus
            />
          </label>

          <div className="room-score-row">
            <label className="room-label room-label--score">
              {match.player1Name || 'Player 1'}
              <input
                className="room-input room-input--score"
                type="number"
                min="0"
                value={score1}
                onChange={e => setScore1(e.target.value)}
              />
            </label>
            <span className="room-score-vs">–</span>
            <label className="room-label room-label--score">
              {match.player2Name || 'Player 2'}
              <input
                className="room-input room-input--score"
                type="number"
                min="0"
                value={score2}
                onChange={e => setScore2(e.target.value)}
              />
            </label>
          </div>

          {error && <p className="room-error">{error}</p>}

          <div className="room-modal-actions">
            <button type="button" className="room-btn room-btn--secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="room-btn room-btn--primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Score'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Match Card ────────────────────────────────────────────────────────────────
// Matches the Flutter app card style: stacked players, score on right, green winner

function MatchCard({ match, onScoreClick, style }) {
  const dblBye = isDoubleBye(match);

  if (dblBye) {
    return (
      <div className="room-match-card room-match-card--bye" style={style}>
        <span className="room-bye-label">BYE</span>
      </div>
    );
  }

  const isComplete = match.status === 'complete';
  const hasScore = match.score1 != null || match.score2 != null;
  const hasPending = !isComplete && match.hasPendingScore;

  // Walkover: completed with a winner but one player slot is empty
  const isBye = isComplete && match.winner &&
                (!match.player1Name || !match.player2Name);

  if (isBye) {
    const walkerName = match.player1Name || match.player2Name || 'TBD';
    const walkerIsP1 = !!match.player1Name;
    return (
      <div className="room-match-card room-match-card--bye-walkover" style={style}>
        <div className={`room-player-row ${walkerIsP1 ? 'room-player-row--winner' : ''}`}>
          <span className="room-player-name">{walkerIsP1 ? walkerName : 'BYE'}</span>
          {walkerIsP1 && <span className="room-winner-dot" />}
        </div>
        <div className="room-card-divider" />
        <div className={`room-player-row ${!walkerIsP1 ? 'room-player-row--winner' : ''}`}>
          <span className="room-player-name">{walkerIsP1 ? 'BYE' : walkerName}</span>
          {!walkerIsP1 && <span className="room-winner-dot" />}
        </div>
      </div>
    );
  }

  const canSubmit = onScoreClick && match.player1Name && match.player2Name &&
                    match.player1Name !== 'TBD' && match.player2Name !== 'TBD' &&
                    !isComplete && !hasPending;

  const p1Winner = match.winner === 'player1';
  const p2Winner = match.winner === 'player2';

  return (
    <div
      className={`room-match-card ${isComplete ? 'room-match-card--complete' : ''} ${hasPending ? 'room-match-card--pending' : ''} ${canSubmit ? 'room-match-card--active' : ''}`}
      style={style}
      onClick={canSubmit ? () => onScoreClick(match) : undefined}
    >
      {/* Player 1 row */}
      <div className={`room-player-row ${p1Winner ? 'room-player-row--winner' : ''} ${p2Winner && isComplete ? 'room-player-row--loser' : ''}`}>
        <span className="room-player-name">{match.player1Name || 'TBD'}</span>
        {hasScore && <span className={`room-player-score ${p1Winner ? 'room-player-score--winner' : ''}`}>{match.score1 ?? 0}</span>}
        {hasPending && <span className="room-player-score room-player-score--pending">{match.pendingScore1 ?? 0}</span>}
        {p1Winner && <span className="room-winner-dot" />}
      </div>

      {/* Divider */}
      <div className="room-card-divider" />

      {/* Player 2 row */}
      <div className={`room-player-row ${p2Winner ? 'room-player-row--winner' : ''} ${p1Winner && isComplete ? 'room-player-row--loser' : ''}`}>
        <span className="room-player-name">{match.player2Name || 'TBD'}</span>
        {hasScore && <span className={`room-player-score ${p2Winner ? 'room-player-score--winner' : ''}`}>{match.score2 ?? 0}</span>}
        {hasPending && <span className="room-player-score room-player-score--pending">{match.pendingScore2 ?? 0}</span>}
        {p2Winner && <span className="room-winner-dot" />}
      </div>

      {/* Pending approval hint */}
      {hasPending && (
        <div className="room-submit-hint room-submit-hint--pending">⏳ Awaiting approval</div>
      )}

      {/* Venue */}
      {match.venueName && (
        <div className="room-match-venue">📍 {match.venueName}</div>
      )}
    </div>
  );
}

// ─── Bracket View with Absolute Positioning ─────────────────────────────────────
// Replicates the Flutter bracket_screen.dart layout algorithm.

function BracketView({ rounds, sortedRounds, onScoreClick, tournamentMeta }) {
  const getRoundLabel = (idx) => {
    const roundKey = String(sortedRounds[idx]);
    if (tournamentMeta?.roundLabels?.[roundKey]) {
      const label = tournamentMeta.roundLabels[roundKey];
      return idx === sortedRounds.length - 1 ? `🏆 ${label}` : label;
    }
    const fromEnd = sortedRounds.length - 1 - idx;
    switch (fromEnd) {
      case 0: return '🏆 Final';
      case 1: return 'Semi-Finals';
      case 2: return 'Quarter-Finals';
      case 3: return 'Round of 16';
      case 4: return 'Round of 32';
      default: return `Round ${idx + 1}`;
    }
  };

  if (sortedRounds.length === 0) return null;

  const { sortedMatches: builtMatches } = buildRoundsWithByes(
    sortedRounds.flatMap(r => rounds[r])
  );

  // Ensure empty virtual rounds are preserved
  const sortedMatches = { ...builtMatches };
  for (const r of sortedRounds) {
    if (!sortedMatches[r]) sortedMatches[r] = [];
  }

  const slotH0 = CARD_H + GAP_V;
  const r0Count = sortedMatches[sortedRounds[0]].length;
  const totalH = r0Count * slotH0;
  const totalRounds = sortedRounds.length;
  const totalW = totalRounds * CARD_W + (totalRounds - 1) * COL_GAP;

  // Build connector data and render as SVG
  const connectorData = buildConnectorLines(sortedRounds, sortedMatches);
  const stroke = '#3a3a5c';
  const sw = 1.5;

  const connectorLines = connectorData.map((c, i) => {
    if (c.type === 'straight') {
      return (
        <g key={`c-${i}`}>
          <line x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2} stroke={stroke} strokeWidth={sw} />
        </g>
      );
    }
    return (
      <g key={`c-${i}`}>
        <line x1={c.rightEdge} y1={c.f1CY} x2={c.midX} y2={c.f1CY} stroke={stroke} strokeWidth={sw} />
        <line x1={c.rightEdge} y1={c.f2CY} x2={c.midX} y2={c.f2CY} stroke={stroke} strokeWidth={sw} />
        <line x1={c.midX} y1={c.f1CY} x2={c.midX} y2={c.f2CY} stroke={stroke} strokeWidth={sw} />
        <line x1={c.midX} y1={c.targetCY} x2={c.nextColLeft} y2={c.targetCY} stroke={stroke} strokeWidth={sw} />
      </g>
    );
  });

  return (
    <div className="room-bracket" style={{ position: 'relative', width: totalW, height: totalH + LABEL_H }}>
      <svg className="room-bracket-svg" width={totalW} height={totalH + LABEL_H}>
        {connectorLines}
      </svg>

      {sortedRounds.map((round, idx) => {
        const colLeft = idx * (CARD_W + COL_GAP);
        const matches = sortedMatches[round];

        return (
          <React.Fragment key={round}>
            <div
              className="room-round-title"
              style={{ position: 'absolute', left: colLeft, top: 0, width: CARD_W }}
            >
              {getRoundLabel(idx)}
            </div>

            {matches.map((match, mi) => {
              const top = cardTop(idx, match.position ?? mi, slotH0, CARD_H) + LABEL_H;
              return (
                <MatchCard
                  key={match.matchId}
                  match={match}
                  onScoreClick={onScoreClick}
                  style={{ position: 'absolute', left: colLeft, top, width: CARD_W, height: CARD_H }}
                />
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Round Robin View ────────────────────────────────────────────────────────────

function RoundRobinView({ matches, tournamentMeta }) {
  const completedMatches = matches.filter(m => m.status === 'complete');
  const rrWinPointsRaw = Number(tournamentMeta?.rrWinPoints);
  const rrDrawPointsRaw = Number(tournamentMeta?.rrDrawPoints);
  const rrWinPoints = Number.isFinite(rrWinPointsRaw) ? rrWinPointsRaw : 3;
  const rrDrawPoints = Number.isFinite(rrDrawPointsRaw) ? rrDrawPointsRaw : 1;

  // Build standings from completed matches (Object.create(null) avoids prototype pollution)
  const standingsMap = Object.create(null);
  const ensurePlayer = (name) => {
    if (!name || name === 'TBD') return;
    if (!standingsMap[name]) {
      standingsMap[name] = { name, played: 0, won: 0, drawn: 0, lost: 0, pf: 0, pa: 0, pts: 0 };
    }
  };

  // Seed all players from all matches (not just completed)
  matches.forEach(m => {
    ensurePlayer(m.player1Name);
    ensurePlayer(m.player2Name);
  });

  completedMatches.forEach(m => {
    const p1 = m.player1Name;
    const p2 = m.player2Name;
    if (!p1 || !p2) return;
    const s1 = standingsMap[p1];
    const s2 = standingsMap[p2];
    s1.played++;
    s2.played++;
    s1.pf += (m.score1 ?? 0);
    s1.pa += (m.score2 ?? 0);
    s2.pf += (m.score2 ?? 0);
    s2.pa += (m.score1 ?? 0);

    if (m.winner == null || m.winner === 'draw') {
      s1.drawn++;
      s2.drawn++;
      s1.pts += rrDrawPoints;
      s2.pts += rrDrawPoints;
    } else if (m.winner === 'player1') {
      s1.won++;
      s2.lost++;
      s1.pts += rrWinPoints;
    } else if (m.winner === 'player2') {
      s2.won++;
      s1.lost++;
      s2.pts += rrWinPoints;
    }
  });

  const standings = Object.values(standingsMap).sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.won !== a.won) return b.won - a.won;
    return (b.pf - b.pa) - (a.pf - a.pa);
  });

  // Group matches by round
  const byRound = {};
  matches.forEach(m => {
    const r = m.round ?? 1;
    if (!byRound[r]) byRound[r] = [];
    byRound[r].push(m);
  });
  const sortedRounds = Object.keys(byRound).map(Number).sort((a, b) => a - b);

  return (
    <div className="rr-view">
      {/* Standings table */}
      <div className="rr-standings">
        <h3 className="rr-section-title">Standings</h3>
        <div className="rr-table-wrap">
          <table className="rr-table">
            <thead>
              <tr>
                <th className="rr-th rr-th--rank">#</th>
                <th className="rr-th rr-th--name">Player</th>
                <th className="rr-th">P</th>
                <th className="rr-th">W</th>
                <th className="rr-th">D</th>
                <th className="rr-th">L</th>
                <th className="rr-th">PF</th>
                <th className="rr-th">PA</th>
                <th className="rr-th rr-th--pts">Pts</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((s, i) => (
                <tr key={s.name} className={i === 0 && s.pts > 0 ? 'rr-row--leader' : ''}>
                  <td className="rr-td rr-td--rank">{i + 1}</td>
                  <td className="rr-td rr-td--name">{s.name}</td>
                  <td className="rr-td">{s.played}</td>
                  <td className="rr-td">{s.won}</td>
                  <td className="rr-td">{s.drawn}</td>
                  <td className="rr-td">{s.lost}</td>
                  <td className="rr-td">{s.pf}</td>
                  <td className="rr-td">{s.pa}</td>
                  <td className="rr-td rr-td--pts">{s.pts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Matches by round */}
      <div className="rr-matches">
        <h3 className="rr-section-title">Matches</h3>
        {sortedRounds.map(round => {
          const roundMatches = byRound[round].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
          return (
            <div key={round} className="rr-round">
              <div className="rr-round-label">Round {round}</div>
              {roundMatches.map(m => {
                const isComplete = m.status === 'complete';
                const isDraw = isComplete && (m.winner == null || m.winner === 'draw');
                const p1Won = m.winner === 'player1';
                const p2Won = m.winner === 'player2';
                return (
                  <div key={m.matchId} className={`rr-match ${isComplete ? 'rr-match--complete' : ''}`}>
                    <span className={`rr-match-player ${p1Won ? 'rr-match-player--winner' : ''} ${p2Won && isComplete ? 'rr-match-player--loser' : ''}`}>
                      {m.player1Name || 'TBD'}
                    </span>
                    <span className="rr-match-score">
                      {isDraw ? (
                        <><span className="rr-draw-badge">Draw</span>{' '}{m.score1 ?? 0} – {m.score2 ?? 0}</>
                      ) : (m.score1 != null || m.score2 != null) ? (
                        `${m.score1 ?? 0} – ${m.score2 ?? 0}`
                      ) : (
                        'vs'
                      )}
                    </span>
                    <span className={`rr-match-player rr-match-player--right ${p2Won ? 'rr-match-player--winner' : ''} ${p1Won && isComplete ? 'rr-match-player--loser' : ''}`}>
                      {m.player2Name || 'TBD'}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function RoomPage() {
  const { code } = useParams();
  const { isAuthenticated } = useAuth();
  const [room, setRoom] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [scoreModal, setScoreModal] = useState(null);
  const [scorerCodeModal, setScorerCodeModal] = useState(null); // match pending scorer code
  const [cachedScorerCode, setCachedScorerCode] = useState(null);
  const [toast, setToast] = useState(null);
  const [champion, setChampion] = useState(null);
  const toastRef = useRef(null);
  const celebratedRef = useRef(false);

  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    setLoading(true);
    getRoomByCode(code)
      .then(data => {
        setRoom(data.room);
        setMatches(data.matches || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [code]);

  const handleMessage = useCallback((event) => {
    // room_info fires on connect — treat as a live signal too
    if (event.type === 'room_info') {
      setConnected(true);
    }
    if (event.type === 'room_update') {
      if (event.data?.matches) setMatches(event.data.matches);
      if (event.data?.tournamentMeta) {
        setRoom(prev => ({ ...prev, tournamentMeta: event.data.tournamentMeta }));
      }
      setConnected(true);
    }
    if (event.type === 'score_resolved') {
      setMatches(prev => applyScoreResolved(prev, event));
      if (event.resolution === 'approved') {
        if (event.winner) {
          const winner = event.winner === 'player1' ? 'player 1' : 'player 2';
          showToast(`✅ Score approved — ${winner} wins!`);
        } else {
          showToast(`✅ Score updated`);
        }
      } else {
        showToast('❌ Score submission was rejected.');
      }
    }
    if (event.type === 'score_submitted') {
      // Show pending scores on the match card immediately
      setMatches(prev => prev.map(m =>
        m.matchId === event.matchId
          ? { ...m, pendingScore1: event.score1, pendingScore2: event.score2, hasPendingScore: true }
          : m
      ));
      showToast('📋 Score submitted — waiting for organiser approval');
    }
  }, []);

  useRoomSocket({
    code: code?.toUpperCase(),
    onMessage: handleMessage,
    onConnect: useCallback(() => setConnected(true), []),
    onDisconnect: useCallback(() => setConnected(false), []),
    enabled: !loading && !error,
  });

  const isRoundRobin = room?.format === 'round-robin' || room?.format === 'roundRobin';

  // Detect tournament champion — last round, single match, has a winner (bracket only)
  useEffect(() => {
    if (isRoundRobin || matches.length === 0) return;
    const maxRound = Math.max(...matches.map(m => m.round ?? 0));
    const finalMatches = matches.filter(m => m.round === maxRound);
    const finalMatch = finalMatches.length === 1 ? finalMatches[0] : null;
    const isComplete = finalMatch?.winner && finalMatch?.status === 'complete';

    if (isComplete) {
      const name = finalMatch.winner === 'player1' ? finalMatch.player1Name : finalMatch.player2Name;
      if (name && !celebratedRef.current) {
        celebratedRef.current = true;
        setChampion(name);
        const duration = 4000;
        const end = Date.now() + duration;
        const interval = setInterval(() => {
          if (Date.now() > end) return clearInterval(interval);
          confetti({ particleCount: 50, spread: 80, origin: { y: 0.3 }, colors: ['#FFD700', '#4CAF50', '#2196F3', '#FF5722', '#9C27B0'] });
        }, 200);
      }
    } else {
      // Final was undone — reset so celebration can fire again
      celebratedRef.current = false;
      setChampion(null);
    }
  }, [matches, isRoundRobin]);

  const rounds = matches.reduce((acc, m) => {
    const r = m.round ?? 1;
    if (!acc[r]) acc[r] = [];
    acc[r].push(m);
    return acc;
  }, {});

  const sortedRounds = Object.keys(rounds).map(Number).sort((a, b) => a - b);

  // Pad missing higher rounds using tournamentMeta from the backend
  const meta = room?.tournamentMeta;
  if (meta?.totalRounds && sortedRounds.length > 0) {
    const baseRound = sortedRounds[0];
    for (let i = 0; i < meta.totalRounds; i++) {
      const r = baseRound + i;
      if (!rounds[r]) {
        rounds[r] = [];
        sortedRounds.push(r);
      }
    }
    sortedRounds.sort((a, b) => a - b);
  }

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="room-page room-page--error">
        <div className="room-not-found">
          <div className="room-not-found-icon">🎱</div>
          <h1>Room not found</h1>
          <p>Check the code and try again.</p>
          <p className="room-code-display">{code?.toUpperCase()}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="room-page">
      <div className="room-header">
        <div className="room-header-top">
          <div className="room-code-badge">{code?.toUpperCase()}</div>
          <div className={`room-live-indicator ${connected ? 'room-live-indicator--live' : ''}`}>
            <span className="room-live-dot" />
            {connected ? 'LIVE' : 'Connecting…'}
          </div>
        </div>
        <h1 className="room-title">{room?.name}</h1>
        {room?.organiserName && (
          <p className="room-organiser">Organised by {room.organiserName}</p>
        )}
      </div>

      {matches.length === 0 ? (
        <div className="room-empty">
          <p>No matches yet — check back soon! 🎱</p>
        </div>
      ) : isRoundRobin ? (
        <div className="room-rr-scroll">
          <RoundRobinView matches={matches} tournamentMeta={room?.tournamentMeta} />
        </div>
      ) : (
        <div className="room-bracket-scroll">
          <BracketView
            rounds={rounds}
            sortedRounds={sortedRounds}
            onScoreClick={isAuthenticated ? (match) => {
              if (room?.hasScorerCode && !cachedScorerCode) {
                setScorerCodeModal(match);
              } else {
                setScoreModal(match);
              }
            } : null}
            tournamentMeta={room?.tournamentMeta}
          />
        </div>
      )}

      {scorerCodeModal && (
        <ScorerCodeModal
          onSubmit={(sc) => {
            setCachedScorerCode(sc);
            setScorerCodeModal(null);
            setScoreModal(scorerCodeModal);
          }}
          onClose={() => setScorerCodeModal(null)}
        />
      )}

      {scoreModal && (
        <ScoreModal
          match={scoreModal}
          roomCode={code}
          scorerCode={cachedScorerCode}
          onClose={() => setScoreModal(null)}
          onSubmitted={() => showToast('Score submitted! Waiting for organiser approval.')}
          onScorerCodeInvalid={() => {
            setCachedScorerCode(null);
            showToast('Invalid scorer code — please try again.');
          }}
        />
      )}

      {toast && <div className="room-toast">{toast}</div>}

      {champion && (
        <div className="room-champion-overlay" onClick={() => setChampion(null)}>
          <div className="room-champion-content">
            <div className="room-champion-trophy">🏆</div>
            <div className="room-champion-name">{champion}</div>
            <div className="room-champion-label">TOURNAMENT CHAMPION</div>
            <div className="room-champion-dismiss">Tap to dismiss</div>
          </div>
        </div>
      )}

      <div className="room-build-version">{__BUILD_VERSION__}</div>
    </div>
  );
}
